import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { FileText, Users, ShoppingBag, AlertTriangle, Scale, Mail } from 'lucide-react';

export default function TermsOfService() {
  const lastUpdated = "March 28, 2026";
  
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20 md:pb-8">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2 flex items-center gap-3">
            <FileText className="h-8 w-8 text-purple-600" />
            Terms of Service
          </h1>
          <p className="text-muted-foreground">
            Last updated: {lastUpdated}
          </p>
        </div>

        <Card className="mb-6">
          <CardContent className="pt-6 prose prose-slate max-w-none">
            <p className="text-lg">
              Welcome to GlobalVyapar. These Terms of Service ("Terms") govern your use of our 
              B2B marketplace platform. By accessing or using GlobalVyapar, you agree to be 
              bound by these Terms.
            </p>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {/* Acceptance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                1. Acceptance of Terms
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-muted-foreground">
                By creating an account or using our platform, you confirm that:
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>You are at least 18 years old</li>
                <li>You have the authority to bind your business to these Terms</li>
                <li>You will comply with all applicable laws and regulations</li>
                <li>All information you provide is accurate and truthful</li>
              </ul>
            </CardContent>
          </Card>

          {/* User Accounts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-green-600" />
                2. User Accounts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Account Registration</h4>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>You must provide accurate business information during registration</li>
                  <li>You are responsible for maintaining the security of your account</li>
                  <li>You must notify us immediately of any unauthorized access</li>
                  <li>One account per business entity is allowed</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Verification</h4>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>GSTIN verification is optional but recommended for trust</li>
                  <li>IEC codes may be verified for export-related businesses</li>
                  <li>We reserve the right to suspend unverified accounts</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Marketplace Rules */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 text-purple-600" />
                3. Marketplace Rules
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">For Sellers</h4>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>List only products you are authorized to sell</li>
                  <li>Provide accurate product descriptions and pricing</li>
                  <li>Respond to inquiries in a timely manner</li>
                  <li>Honor commitments made through the platform</li>
                  <li>Do not list prohibited or illegal items</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">For Buyers</h4>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Send genuine business inquiries only</li>
                  <li>Provide accurate requirements and contact information</li>
                  <li>Negotiate in good faith</li>
                  <li>Complete transactions as agreed</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* AI Features */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5 text-amber-600" />
                4. AI-Powered Features
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-muted-foreground">
                Our platform uses AI to assist with various features:
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>AI-generated product descriptions are suggestions only</li>
                <li>Negotiation assistance provides guidance, not binding offers</li>
                <li>All AI drafts should be reviewed before sending</li>
                <li>Final business decisions remain with the users</li>
                <li>We are not liable for decisions based on AI suggestions</li>
              </ul>
              <p className="text-sm bg-amber-50 p-3 rounded-lg border border-amber-200 mt-3">
                <strong>AI suggests, you decide.</strong> All AI-generated content is editable 
                and should be reviewed before use in business communications.
              </p>
            </CardContent>
          </Card>

          {/* Prohibited Activities */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                5. Prohibited Activities
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-3">You agree not to:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Provide false or misleading information</li>
                <li>Engage in fraudulent or deceptive practices</li>
                <li>Spam or send unsolicited communications</li>
                <li>Attempt to circumvent platform fees</li>
                <li>Collect user data without authorization</li>
                <li>Interfere with platform security or functionality</li>
                <li>List counterfeit or illegal products</li>
                <li>Harass other users</li>
              </ul>
            </CardContent>
          </Card>

          {/* Limitation of Liability */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5 text-slate-600" />
                6. Limitation of Liability
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-muted-foreground">
                GlobalVyapar provides a platform for B2B connections. We are not a party to 
                transactions between users and are not liable for:
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Quality of products or services offered by sellers</li>
                <li>Disputes between buyers and sellers</li>
                <li>Payment issues or fraud between parties</li>
                <li>Loss of business or opportunities</li>
                <li>Technical issues or platform downtime</li>
              </ul>
            </CardContent>
          </Card>

          {/* Termination */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                7. Account Termination
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-3">
                We may suspend or terminate your account if you:
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Violate these Terms of Service</li>
                <li>Engage in prohibited activities</li>
                <li>Provide false verification documents</li>
                <li>Receive multiple complaints from other users</li>
                <li>Fail to respond to inquiries repeatedly</li>
              </ul>
              <p className="text-muted-foreground mt-3">
                You may delete your account at any time through your profile settings.
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-purple-600" />
                8. Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-3">
                For questions about these Terms, contact us at:
              </p>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="font-semibold">GlobalVyapar</p>
                <p className="text-muted-foreground">Email: support@globalvyapar.co.in</p>
                <p className="text-muted-foreground">Website: www.globalvyapar.co.in</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Separator className="my-8" />
        
        <p className="text-center text-sm text-muted-foreground">
          By using GlobalVyapar, you agree to these Terms of Service.
        </p>
      </div>
    </Layout>
  );
}
